/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module DnsModule
 */

import {
    Component,
    Type,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import { DnsPolicy } from 'object-types';
import { AviPermissionResource } from 'generated-types';
import { withFullModalMixin } from 'ajs/js/utilities/mixins/with-full-modal.mixin';
import { withEditChildMessageItemMixin }
    from 'ajs/modules/data-model/mixins/with-edit-child-message-item.mixin';
import { RepeatedMessageItem } from
    'ajs/modules/data-model/factories/repeated-message-item.factory';
import { ObjectTypeItem }
    from 'ajs/modules/data-model/factories/object-type-item.factory';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';
import { DnsRuleConfigItem } from './dns-rule.config-item.factory';
import { IExtendedDnsPolicy } from './dns-policy.item.types';

import * as l10n from '../dns.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Used for defining type of data.
 */
interface IDnsPolicyData {
    config: IExtendedDnsPolicy;
}

/**
 * @description
 *
 *   DNS Policy item.
 *
 * @author Abhinesh Gour
 */
export class DnsPolicyItem extends
    withEditChildMessageItemMixin(withFullModalMixin(ObjectTypeItem)) {
    public static ajsDependencies = [
        'l10nService',
    ];

    /** Overriding getConfig()'s return type */
    public getConfig: () => IExtendedDnsPolicy;

    /**
     * Dns Policy Data.
     */
    public data: IDnsPolicyData;

    private readonly l10nService: L10nService;

    constructor(args: Record<string, any> = {}) {
        const extendedArgs = {
            objectName: 'dnspolicy',
            objectType: DnsPolicy,
            windowElement: 'lazy-load',
            permissionName: AviPermissionResource.PERMISSION_DNSPOLICY,
            ...args,
        };

        super(extendedArgs);

        this.l10nService = this.getAjsDependency_('l10nService');
        this.l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Getter for Dns Policy Rule.
     */
    public get rule(): RepeatedMessageItem<DnsRuleConfigItem> {
        return this.getConfig().rule;
    }

    /**
     * Method to add Dns Policy Rule.
     */
    public addDnsPolicyRule(): void {
        this.addChildMessageItem({
            field: 'rule',
            modalBindings: {
                isEditing: false,
            },
        });
    }

    /**
     * Triggers edit for Dns Policy Rule.
     */
    public editDnsPolicyRule(rule: DnsRuleConfigItem): void {
        this.editChildMessageItem({
            field: 'rule',
            messageItem: rule,
            modalBindings: {
                isEditing: true,
            },
        });
    }

    /**
     * Deletes Dns Policy Rule.
     */
    public deleteDnsPolicyRule(rule: DnsRuleConfigItem): void {
        this.rule.removeByMessageItem(rule);
    }

    /**
     * Method used to import lazy loaded modal component.
     */
    // eslint-disable-next-line class-methods-use-this
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const {
            DnsPolicyModalComponent,
        } = await import(
            /* webpackChunkName: "dns-policy-modal" */
            // eslint-disable-next-line max-len
            'ng/lazy-loaded-components/modals/dns-policy-modal/dns-policy-modal.component'
        );

        return DnsPolicyModalComponent as Type<Component>;
    }

    /** @override */
    protected getModalBreadcrumbTitle(): string {
        return this.l10nService.getMessage(l10nKeys.dnsPolicyModalBreadcrumbTitle);
    }
}
